// ===========================================================================
// Shared video for a group room.
//
// The room owner pins a video and everyone watches the same moment of it. What
// is shared is the POSITION, not the video: each viewer streams it themselves.
// Lumi never fetches, stores or relays the media -- doing so would be
// redistributing somebody else's work, and would flatten the server besides.
//
// Only the id is ever accepted from a client. A URL is parsed here, strictly,
// and everything but an eleven-character YouTube id is refused -- so nothing a
// user types can become part of a URL the browser then loads.
// ASCII only.
// ===========================================================================

import { parseVideoUrl } from './video-providers.js';

/**
 * The YouTube id in a link, or null.
 *
 * Kept because plenty of code only cares about YouTube, but it is now a thin
 * read of the shared registry rather than a second, separate idea of what a
 * YouTube link looks like. Two parsers that must agree eventually will not.
 *
 * @param {string} input
 * @returns {string|null}
 */
export function parseYouTubeId(input) {
  if (typeof input !== 'string') return null;
  // A bare id is meaningful only once a provider has been named -- which this
  // function does by existing. parseVideoUrl stays strict about it on purpose:
  // there, an eleven-character string could belong to anybody.
  const bare = parseVideoUrl(`youtube:${input.trim()}`);
  if (bare) return bare.videoId;
  const hit = parseVideoUrl(input);
  return hit && hit.provider === 'youtube' ? hit.videoId : null;
}

/** What a client may ask to do to the shared video. */
export const VideoAction = Object.freeze({
  PLAY: 'play',
  PAUSE: 'pause',
  SEEK: 'seek',
  STOP: 'stop',
});

export const VIDEO_ACTIONS = Object.freeze(Object.values(VideoAction));

/**
 * May this member perform this action?
 *
 * The owner can do everything. When control is SHARED, everyone else may play
 * and pause -- the two that only affect when you are all watching -- but not
 * seek or stop, which move the room somewhere it did not agree to go or end it
 * outright. With sharing off, the owner alone decides.
 *
 * Exported and pure so the rule can be tested on its own, rather than only
 * through a room.
 *
 * @param {{ isOwner: boolean, sharedControl: boolean }} who
 * @param {string} action
 */
export function mayControlVideo({ isOwner, sharedControl }, action) {
  if (!VIDEO_ACTIONS.includes(action)) return false;
  if (isOwner) return true;
  if (!sharedControl) return false;
  return action === VideoAction.PLAY || action === VideoAction.PAUSE;
}

export default { parseYouTubeId, mayControlVideo, VideoAction, VIDEO_ACTIONS };
